import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './Card';
import { Button } from './Button';
import { PricingPlan } from '@/types';
import { cn } from '@/utils/cn';

interface EnterprisePricingCardProps {
  plan: PricingPlan;
  featured?: boolean;
}

export const EnterprisePricingCard: FC<EnterprisePricingCardProps> = ({ 
  plan, 
  featured = false 
}) => {
  // 根据企业套餐等级设置不同的样式
  const getPlanStyles = () => {
    switch (plan.id) {
      case 'enterprise':
        return {
          cardClass: 'bg-gradient-to-br from-blue-50 via-white to-purple-50 border-2 border-blue-500 shadow-2xl shadow-blue-200 ring-2 ring-blue-300 ring-offset-2',
          badgeClass: 'bg-gradient-to-r from-blue-600 to-purple-600',
          headerClass: 'text-blue-700',
          iconClass: 'text-blue-600',
          buttonClass: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-xl',
          badge: '🏢 企业定制'
        };
      default:
        return {
          cardClass: 'bg-gradient-to-br from-gray-50 via-white to-slate-50 border-2 border-gray-400',
          badgeClass: 'bg-gradient-to-r from-gray-600 to-slate-600',
          headerClass: 'text-gray-700',
          iconClass: 'text-gray-600',
          buttonClass: 'bg-gradient-to-r from-gray-600 to-slate-600 hover:from-gray-700 hover:to-slate-700 text-white',
          badge: '🏢 企业版'
        };
    }
  };

  const styles = getPlanStyles();

  // 获取企业方案图标
  const getPlanIcon = () => {
    switch (plan.id) {
      case 'enterprise':
        return '🏢';
      default:
        return '🏢';
    }
  };

  return (
    <Card className={cn(
      'relative transform transition-all duration-300 hover:scale-105',
      styles.cardClass,
      featured && 'scale-105 shadow-2xl'
    )}>
      {/* Plan Badge */}
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
        <span className={cn(
          'text-white px-4 py-1.5 text-sm font-bold rounded-full shadow-lg',
          styles.badgeClass
        )}>
          {styles.badge}
        </span>
      </div>

      <div className="text-center pt-6">
        {/* Plan Icon */}
        <div className="text-6xl mb-4">
          {getPlanIcon()}
        </div>

        {/* Plan Name */}
        <h3 className={cn(
          'text-3xl font-bold mb-2',
          styles.headerClass
        )}>
          {plan.name}
        </h3>

        {/* Team Size */}
        <div className="mb-6">
          <div className={cn(
            'inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold',
            'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 border border-gray-300'
          )}>
            <span className="mr-2">👥</span>
            {plan.teamSize}
          </div>
        </div>

        {/* Pricing Display */}
        <div className="mb-8">
          <div className="text-center">
            <div className={cn(
              'text-4xl font-bold mb-2',
              styles.headerClass
            )}>
              定制价格
            </div>
            <p className="text-gray-600 text-sm">
              根据团队规模和需求定制
            </p>
          </div>
        </div>

        {/* SLA Level */}
        {plan.slaLevel && (
          <div className="mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center justify-center space-x-2">
                <span className="text-green-600">🛡️</span>
                <span className="text-green-700 font-semibold text-sm">
                  {plan.slaLevel}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        <p className="text-gray-600 text-sm mb-8 px-2 leading-relaxed">
          {plan.description}
        </p>

        {/* Core Features */}
        <div className="text-left mb-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <span className={cn('mr-2', styles.iconClass)}>✨</span>
            核心功能
          </h4>
          <ul className="space-y-3">
            {plan.features.slice(0, 4).map((feature, index) => (
              <li key={index} className="flex items-start space-x-3">
                <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
              </li>
            ))}
            {plan.features.length > 4 && (
              <li className="text-sm text-gray-500 pl-6">
                还有 {plan.features.length - 4} 项核心功能...
              </li>
            )}
          </ul>
        </div>

        {/* Enterprise Features */}
        {plan.enterpriseFeatures && plan.enterpriseFeatures.length > 0 && (
          <div className="text-left mb-8">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
              <span className={cn('mr-2', styles.iconClass)}>🚀</span>
              企业专属
            </h4>
            <ul className="space-y-3">
              {plan.enterpriseFeatures.slice(0, 3).map((feature, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <span className={cn('mt-0.5 flex-shrink-0', styles.iconClass)}>⭐</span>
                  <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                </li>
              ))}
              {plan.enterpriseFeatures.length > 3 && (
                <li className="text-sm text-gray-500 pl-6">
                  还有 {plan.enterpriseFeatures.length - 3} 项企业专属功能...
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Support Info */}
        <div className="mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="text-blue-600 text-lg mt-0.5">🎯</span>
              <div className="text-left">
                <p className="text-blue-800 font-semibold text-sm mb-1">专属服务</p>
                <p className="text-blue-700 text-xs leading-relaxed">
                  {plan.support}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <Button
          as={Link}
          to="/contact"
          className={cn(
            'w-full font-bold py-4 text-lg transform transition-all duration-300 hover:scale-105',
            styles.buttonClass
          )}
        >
          联系销售咨询
        </Button>

        {/* Additional Info */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-600 leading-relaxed">
            💡 企业方案支持POC试用，提供专业技术评估和定制化解决方案
          </p>
        </div>
      </div>
    </Card>
  );
};